import { Box, Flex, useOutsideClick } from '@chakra-ui/react';
import { useResources } from 'datx-jsonapi-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLecturePageQueryParams } from '@/components/ui/features/lecture/hooks';
import { OmnisearchAutocorrect } from '@/components/ui/features/lecture/LectureOmnisearch/components/OmnisearchAutocorrect';
import { OmnisearchDownloadableOccurrences } from '@/components/ui/features/lecture/LectureOmnisearch/components/OmnisearchDownloadableOccurrences';
import { OmnisearchSearchInput } from '@/components/ui/features/lecture/LectureOmnisearch/components/OmnisearchSearchInput';
import { OmnisearchVideoPosters } from '@/components/ui/features/lecture/LectureOmnisearch/components/OmnisearchVideoPosters';
import { useAnalytics } from '@/context/Analytics';
import { useSearch } from '@/hooks/use-search/useSearch';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { queryLectureOmnisearchOccurrences } from '@/queries/omnisearch-occurrences';
import { OmnisearchOccurrence } from '@/resources/OmnisearchOccurrence';

export const LectureOmnisearch = () => {
  const modalRef = useRef();
  const { t: tr } = useTranslation();
  const { query, t, index, forcedQuery } = useQueryParams<{
    query: string;
    t: number;
    index: number;
    forcedQuery: string;
  }>({
    query: StringParam,
    t: StringParam,
    index: StringParam,
    forcedQuery: StringParam,
  });
  const { id: lectureId } = useLecturePageQueryParams();
  const { getSearchInputProps } = useSearch({ scroll: true });
  const { ref, defaultValue } = getSearchInputProps();
  const [isAutocompleteOpen, setAutocompleteOpen] = useState(false);
  const analytics = useAnalytics();

  const disabledAutocorrect = forcedQuery === query;

  const { data, meta } = useResources<OmnisearchOccurrence, { query: string; autocorrected: boolean }>(
    queryLectureOmnisearchOccurrences(lectureId, {
      query,
      disable_autocorrect: String(disabledAutocorrect),
    })
  );

  useOutsideClick({
    ref: modalRef,
    handler: () => setAutocompleteOpen(false),
  });

  useEffect(() => {
    if (query) {
      setAutocompleteOpen(true);
      analytics.track('lecture_omnisearch_search', {
        query,
        lectureId,
      });
    }
  }, [query]);

  useEffect(() => {
    setAutocompleteOpen(false);
  }, [t, index]);

  return (
    <Flex position="relative" zIndex="20" mb="8" justifyContent="center">
      <Box ref={modalRef} w={{ base: '335px', md: '686px', lg: '794px' }} position="relative">
        {isAutocompleteOpen && query && (
          <Box
            border="1px solid gray"
            borderColor="gray.200"
            position="absolute"
            pt="6"
            top="4"
            left="0"
            w="100%"
            bg="gray.50"
            borderRadius="4px"
          >
            <OmnisearchAutocorrect autocorrected={meta?.autocorrected} autocorrectedQuery={meta?.query} />
            <OmnisearchVideoPosters autocorrectedQuery={meta?.query} data={data} />
            <OmnisearchDownloadableOccurrences data={data} />
          </Box>
        )}
        <OmnisearchSearchInput
          isAutocompleteOpen={isAutocompleteOpen}
          data={data}
          ref={ref}
          placeholder={tr('lectureOmnisearch.searchInputPlaceholder')}
          defaultValue={defaultValue}
          onFocus={() => query && setAutocompleteOpen(true)}
          w="full"
          bg="white"
          color="gray.700"
          borderRadius="full"
          key={lectureId}
        />
      </Box>
    </Flex>
  );
};
